import {
  _daylightSavingAdjust,
  IDaylightSavingAdjust,
} from './DexeroGeneralHelpers/_daylightSavingAdjust';
import { _ticksTo1970, ITicksTo1970 } from './DexeroGeneralHelpers/_ticksTo1970';
import {
  cleanEmptyArrayElms,
  ICleanEmptyArrayElms,
} from './DexeroGeneralHelpers/cleanEmptyArrayElms';
import {
  convertStringToNumber,
  IConvertStringToNumber,
} from './DexeroGeneralHelpers/convertStringToNumber';
import { escapeJSON, IEscapeJSON } from './DexeroGeneralHelpers/escapeJSON';
import { formatTime, IFormatTime } from './DexeroGeneralHelpers/formatTime';
import {
  generateWebservicePostJson,
  IGenerateWebservicePostJson,
} from './DexeroGeneralHelpers/generateWebservicePostJson';
import { getDaysInMonth, IGetDaysInMonth } from './DexeroGeneralHelpers/getDaysInMonth';
import {
  getDifferenceOfArrays,
  IGetDifferenceOfArrays,
} from './DexeroGeneralHelpers/getDifferenceOfArrays';
import {
  getDifferenceOfNums,
  IGetDifferenceOfNums,
} from './DexeroGeneralHelpers/getDifferenceOfNums';
import {
  getFieldIdentifierForId,
  IGetFieldIdentifierForId,
} from './DexeroGeneralHelpers/getFieldIdentifierForId';
import { getFormValuesObj, IGetFormValuesObj } from './DexeroGeneralHelpers/getFormValuesObj';
import {
  getNameOfClassStartingWith,
  IGetNameOfClassStartingWith,
} from './DexeroGeneralHelpers/getNameOfClassStartingWith';
import {
  getObjectValueByKey,
  IGetObjectValueByKey,
} from './DexeroGeneralHelpers/getObjectValueByKey';
import { getRandomInteger, IGetRandomInteger } from './DexeroGeneralHelpers/getRandomInteger';
import {
  getTimeFormatPositions,
  IGetTimeFormatPositions,
} from './DexeroGeneralHelpers/getTimeFormatPositions';
import {
  getFieldIdForIdentifier,
  IGetFieldIdForIdentifier,
} from './DexeroGeneralHelpers/getFieldIdForIdentifier';
import {
  getUniqueArrayValues,
  IGetUniqueArrayValues,
} from './DexeroGeneralHelpers/getUniqueArrayValues';
import { getUUID, IGetUUID } from './DexeroGeneralHelpers/getUUID';
import { INormalizeText, normalizeText } from './DexeroGeneralHelpers/normalizeText';
import {
  IOpenFileSystemBrowser,
  openFileSystemBrowser,
} from './DexeroGeneralHelpers/openFileSystemBrowser';
import { IPadWithSpace, padWithSpace } from './DexeroGeneralHelpers/padWithSpace';
import { IPadWithZero, padWithZero } from './DexeroGeneralHelpers/padWithZero';
import { IParseDate, parseDate } from './DexeroGeneralHelpers/parseDate';
import { IParseTime, parseTime } from './DexeroGeneralHelpers/parseTime';
import { IRedirectToPage, redirectToPage } from './DexeroGeneralHelpers/redirectToPage';
import { IReloadPage, reloadPage } from './DexeroGeneralHelpers/reloadPage';
import {
  IRemoveArrayItemByName,
  removeArrayItemByName,
} from './DexeroGeneralHelpers/removeArrayItemByName';
import {
  IRemoveLastCharacterOf,
  removeLastCharacterOf,
} from './DexeroGeneralHelpers/removeLastCharacterOf';
import {
  IRemoveObjectPropByKey,
  removeObjectPropByKey,
} from './DexeroGeneralHelpers/removeObjectPropByKey';
import {
  IRemoveSpacesAndLineBreaks,
  removeSpacesAndLineBreaks,
} from './DexeroGeneralHelpers/removeSpacesAndLineBreaks';
import { IStripHtmlComments, stripHtmlComments } from './DexeroGeneralHelpers/stripHtmlComments';
import {
  ISyntaxHighlightJson,
  syntaxHighlightJson,
} from './DexeroGeneralHelpers/syntaxHighlightJson';
import { IValidateRegEx, validateRegEx } from './DexeroGeneralHelpers/validateRegEx';

export interface IDexeroGeneralHelpers {
  _daylightSavingAdjust: IDaylightSavingAdjust;
  _ticksTo1970: ITicksTo1970;
  cleanEmptyArrayElms: ICleanEmptyArrayElms;
  convertStringToNumber: IConvertStringToNumber;
  escapeJSON: IEscapeJSON;
  formatTime: IFormatTime;
  generateWebservicePostJson: IGenerateWebservicePostJson;
  getDaysInMonth: IGetDaysInMonth;
  getDifferenceOfArrays: IGetDifferenceOfArrays;
  getDifferenceOfNums: IGetDifferenceOfNums;
  getFieldIdentifierForId: IGetFieldIdentifierForId;
  getFieldIdForIdentifier: IGetFieldIdForIdentifier;
  getFormValuesObj: IGetFormValuesObj;
  getNameOfClassStartingWith: IGetNameOfClassStartingWith;
  getObjectValueByKey: IGetObjectValueByKey;
  getRandomInteger: IGetRandomInteger;
  getTimeFormatPositions: IGetTimeFormatPositions;
  getUniqueArrayValues: IGetUniqueArrayValues;
  getUUID: IGetUUID;
  normalizeText: INormalizeText;
  openFileSystemBrowser: IOpenFileSystemBrowser;
  padWithSpace: IPadWithSpace;
  padWithZero: IPadWithZero;
  parseDate: IParseDate;
  parseTime: IParseTime;
  redirectToPage: IRedirectToPage;
  reloadPage: IReloadPage;
  removeArrayItemByName: IRemoveArrayItemByName;
  removeLastCharacterOf: IRemoveLastCharacterOf;
  removeObjectPropByKey: IRemoveObjectPropByKey;
  removeSpacesAndLineBreaks: IRemoveSpacesAndLineBreaks;
  stripHtmlComments: IStripHtmlComments;
  syntaxHighlightJson: ISyntaxHighlightJson;
  validateRegEx: IValidateRegEx;
}

const DexeroGeneralHelpers: IDexeroGeneralHelpers = {
  // Adjust time for daylight savings
  _daylightSavingAdjust,

  // Number of seconds from 1970
  _ticksTo1970,

  // Remove empty array elements from array
  cleanEmptyArrayElms,

  // Remove invalid characters from number and parse float
  convertStringToNumber,

  //  Format time
  formatTime,

  // Remove invalid characters for JSON value
  escapeJSON,

  // Transform HTML form values into valid webservice post OBJ
  generateWebservicePostJson,

  // Get number of days in a month
  getDaysInMonth,

  // Get difference between two arrays
  getDifferenceOfArrays,

  // Get difference between two numbers
  getDifferenceOfNums,

  // Get field id for given field identifier
  getFieldIdForIdentifier,

  // Get field identifier for given field id
  getFieldIdentifierForId,

  // get key => value object of form values
  getFormValuesObj,

  // Get name of class starting with *** (Useful to get exact class of element with class that can be variable cols-md-*)
  getNameOfClassStartingWith,

  // Get object value by key
  getObjectValueByKey,

  // Generate a random integer between min (inclusive) and max (inclusive)
  getRandomInteger,

  //  Get time format position
  getTimeFormatPositions,

  // Get unique array values
  getUniqueArrayValues,

  // Generate UUID
  getUUID,

  // Strip accents from string and replace with normal characters
  normalizeText,
  //  Open a file browser for a webdav url
  openFileSystemBrowser,

  // Pad a string with spaces to desired length
  padWithSpace,

  // Pad a string with zeros to desired length
  padWithZero,

  // Parse date
  parseDate,

  // Parse time
  parseTime,

  // Redirect to a page
  redirectToPage,

  // Refresh a page
  reloadPage,

  // Remove array item by key(name) val
  removeArrayItemByName,

  // Remove last specific character from a string
  removeLastCharacterOf,

  // Remove object property by key
  removeObjectPropByKey,

  // Remove surrounding spaces and returns from string
  removeSpacesAndLineBreaks,

  // Remove comments from string
  stripHtmlComments,

  // Convert json into html with highlighting
  syntaxHighlightJson,

  // Validate REGEX
  validateRegEx,
};

export default DexeroGeneralHelpers;
